import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import i18n from '../../i18n';
import {
  useProductivityTrimesters,
  type ProductivityTrimesters, type TrimesterBucket, type TrimesterKey,
} from '../../api/productivityTrimesters';

/**
 * The productivity drill-through (docs/specs/productivity-client-month.md §5).
 *
 * A segment opens the rows BEHIND it — the reviews that give its months their colour, or for gray
 * the clients with an uncovered month. The URL carries a KEY (`drill=2026.T3.covered[.staffId]`), not
 * the ids: the ids are resolved from the same productivity query the chart drew from, so a copied
 * link reproduces the same view, and the segment's count and the target's rows come from ONE list.
 * A second filter that approximates the set (status + period overlap) could not agree: it would
 * include reviews of clients outside the roster, months outside a client's window, and pending
 * reviews on months a completed review already makes green.
 */

export type DrillField = 'covered' | 'inProgress' | 'pending' | 'uncovered';

export interface DrillKey {
  year: number;
  trimester: TrimesterKey;
  field: DrillField;
  /** A reviewer's staff id, for the per-supervisor bars. */
  staffId?: string;
}

const FIELDS: readonly DrillField[] = ['covered', 'inProgress', 'pending', 'uncovered'];
const TRIMESTERS: readonly TrimesterKey[] = ['T1', 'T2', 'T3'];

export const DRILL_PARAM = 'drill';

export function buildDrillParam(k: DrillKey): string {
  return [k.year, k.trimester, k.field, ...(k.staffId ? [k.staffId] : [])].join('.');
}

export function parseDrillParam(raw: string | null): DrillKey | null {
  if (!raw) return null;
  const [y, t, f, ...rest] = raw.split('.');
  const year = Number(y);
  if (!Number.isInteger(year) || year < 2000 || year > 2100) return null;
  if (!TRIMESTERS.includes(t as TrimesterKey) || !FIELDS.includes(f as DrillField)) return null;
  const staffId = rest.join('.') || undefined;
  return { year, trimester: t as TrimesterKey, field: f as DrillField, staffId };
}

/** Where a segment opens: the Reviews grid, or for gray the Clients grid (no review to open). */
export function drillHref(k: DrillKey): string {
  const base = k.field === 'uncovered' ? '/clients' : '/reviews';
  // `view=all` is explicit: a bare URL applies the grid's default view (My Active on Reviews, per
  // contract v2 V3), which would hide completed reviews and every other reviewer's rows.
  return `${base}?view=all&${DRILL_PARAM}=${encodeURIComponent(buildDrillParam(k))}`;
}

/** "months reviewed" / "meses revisados" — the segment, in the UI language (`drill.field.*`). */
const fieldWords = (field: DrillField): string => i18n.t(`drill.field.${field}`);

export interface ResolvedDrill {
  ids: ReadonlySet<string>;
  /** "T3 2026 · months reviewed · Helen Gomez" */
  label: string;
  /** The segment's month count, so the banner can say what the rows explain. */
  months: number;
}

function bucketFor(data: ProductivityTrimesters, k: DrillKey): { bucket?: TrimesterBucket; who?: string } {
  if (!k.staffId) return { bucket: data.trimesters.find((t) => t.key === k.trimester) };
  const sup = data.bySupervisor.find((s) => s.staffId === k.staffId);
  return { bucket: sup?.trimesters.find((t) => t.key === k.trimester), who: sup?.name };
}

export function resolveDrill(data: ProductivityTrimesters, k: DrillKey): ResolvedDrill | null {
  const { bucket, who } = bucketFor(data, k);
  if (!bucket?.drill || !bucket.months) return null;
  const ids = k.field === 'uncovered' ? bucket.drill.uncoveredClients : bucket.drill[k.field];
  return {
    ids: new Set(ids.map((i) => i.toLowerCase())),
    // A GETTER, read at render: the resolved drill is memoised on the URL and the data, so a value
    // computed here would keep the language it was first built in after a toggle.
    get label() {
      return [`${k.trimester} ${k.year}`, fieldWords(k.field), who].filter(Boolean).join(' · ');
    },
    months: bucket.months[k.field],
  };
}

export type DrillState =
  | { kind: 'none' }
  | { kind: 'invalid'; raw: string }
  | { kind: 'loading'; key: DrillKey }
  | { kind: 'unavailable'; key: DrillKey }
  | { kind: 'ready'; key: DrillKey; drill: ResolvedDrill };

/**
 * The drill in the current URL. While it resolves, a page must show NO rows rather than every row:
 * "loading" briefly showing the unfiltered grid is exactly the moment someone reads the wrong count.
 */
export function useProductivityDrill(): { state: DrillState; clear: () => void } {
  const [params, setParams] = useSearchParams();
  const raw = params.get(DRILL_PARAM);
  const key = useMemo(() => parseDrillParam(raw), [raw]);
  // The year is only known once the key parses; a disabled query when there is none.
  const query = useProductivityTrimesters(key?.year ?? 0, { enabled: !!key });
  const clear = () =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(DRILL_PARAM);
      return next;
    }, { replace: true });

  // Stable between renders, so the rows a page derives from it are not recomputed on every render.
  const state = useMemo((): DrillState => {
    if (!raw) return { kind: 'none' };
    if (!key) return { kind: 'invalid', raw };
    if (!query.data) return query.isError ? { kind: 'unavailable', key } : { kind: 'loading', key };
    const drill = resolveDrill(query.data, key);
    return drill ? { kind: 'ready', key, drill } : { kind: 'unavailable', key };
  }, [raw, key, query.data, query.isError]);

  return { state, clear };
}

/** Rows a drill admits. `none` and `invalid` admit everything; the page still shows the banner. */
export function applyDrill<T>(rows: readonly T[], state: DrillState, idOf: (row: T) => string | null | undefined): T[] {
  if (state.kind === 'none' || state.kind === 'invalid') return [...rows];
  if (state.kind !== 'ready') return [];
  return rows.filter((r) => {
    const id = idOf(r);
    return !!id && state.drill.ids.has(id.toLowerCase());
  });
}
